/**
 * Auth Service
 * Handles authentication operations
 */

import { authAPI, deviceTokensAPI, tokenManager } from '../utils/api';
import {
  withErrorHandling,
  extractResponseItem
} from './base.service.js';
import { STORAGE_KEYS } from '../config/app';
import { setSignInNotice } from '../utils/signInNotice';
import { sessionEndedNotice } from '../utils/sessionEnd';

const STORAGE_KEY = STORAGE_KEYS.USER;

/**
 * Store user in localStorage
 * @param {Object} user - User object to store
 */
function cacheUser(user) {
  if (user) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  }
}

/**
 * Clear cached user from localStorage
 */
function clearUserCache() {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Get cached user from localStorage
 * @returns {Object|null} Cached user or null
 */
function getCachedUser() {
  const cached = localStorage.getItem(STORAGE_KEY);
  return cached ? JSON.parse(cached) : null;
}

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User object
 */
export async function login(email, password) {
  return withErrorHandling(async () => {
    const response = await authAPI.login(email, password);
    const user = extractResponseItem(response, 'user');
    cacheUser(user);
    return user;
  }, 'Login');
}

/**
 * Sign in with the device token a phone holds instead of a password.
 *
 * Ends in the identical state to `login`: the session JWT stored by the api
 * layer and the same user object cached here, because the server hands back the
 * same envelope. It also returns the ROTATED device token — the old one is dead
 * the moment this resolves, so the caller must write the new one to the secure
 * store.
 *
 * Deliberately not wrapped in `withErrorHandling`: that helper rethrows a fresh
 * Error and drops `status`, and the caller has to tell "the server refused this
 * token" (forget it, ask for the password) from "the phone is offline" (keep it,
 * offer a retry).
 *
 * @param {string} deviceToken
 * @returns {Promise<{user: Object, deviceToken: {token: string, id: number, expires_at: string}}>}
 */
export async function loginWithDeviceToken(deviceToken) {
  try {
    const response = await deviceTokensAPI.exchange(deviceToken);
    const user = extractResponseItem(response, 'user');
    cacheUser(user);
    return { user, deviceToken: response.device_token };
  } catch (error) {
    console.error('[Service Error] Device sign-in:', error);
    throw error;
  }
}

/**
 * Logout current user
 */
export async function logout() {
  authAPI.logout();
  clearUserCache();
}

/**
 * Get current authenticated user
 * @returns {Promise<Object|null>} User object or null
 */
export async function getCurrentUser() {
  const cachedUser = getCachedUser();

  if (!cachedUser) {
    return null;
  }

  // If we have a token, verify it's still valid
  if (tokenManager.hasToken()) {
    try {
      const response = await authAPI.getCurrentUser();
      const user = extractResponseItem(response, 'user');
      cacheUser(user);
      return user;
    } catch (error) {
      // Only a refused token ends the session. Offline, a server outage or a
      // timeout keeps the signed-in user; the next successful call re-checks.
      if (error?.status === 401 || error?.status === 403) {
        console.warn('Token refused, clearing auth cache');
        // Say why on the sign-in page — suspended, or the password or email
        // changed elsewhere — instead of an unexplained sign-out (audit A, M8).
        setSignInNotice(sessionEndedNotice(error.data?.error || error.message));
        clearUserCache();
        tokenManager.removeToken();
        return null;
      }
      return cachedUser;
    }
  }

  return cachedUser;
}

/**
 * Register new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} Created user object
 */
export async function register(userData) {
  return withErrorHandling(async () => {
    const response = await authAPI.register(userData);
    const user = extractResponseItem(response, 'user');
    cacheUser(user);
    return user;
  }, 'Registration');
}

/**
 * Role hierarchy for permission checking
 */
const ROLE_HIERARCHY = {
  student: 1,
  view_only_staff: 2,
  staff: 2,
  accounts_officer: 2,
  it_support: 2,
  department_admin: 3,
  head_of_department: 3,
  master_admin: 4
};

/**
 * Check if user has permission for required role
 * @param {Object} user - User object
 * @param {string} requiredRole - Required role
 * @returns {boolean} True if user has permission
 */
function hasPermission(user, requiredRole) {
  return (ROLE_HIERARCHY[user?.role] || 0) >= (ROLE_HIERARCHY[requiredRole] || 0);
}

/**
 * Check if user is admin (department_admin or master_admin)
 * @param {Object} user - User object
 * @returns {boolean} True if user is admin
 */
export function isAdmin(user) {
  return hasPermission(user, 'department_admin');
}

/**
 * Check if user is master admin
 * @param {Object} user - User object
 * @returns {boolean} True if user is master admin
 */
export function isMasterAdmin(user) {
  return user?.role === 'master_admin';
}

// Export object-style service for backwards compatibility
export const authService = {
  login,
  loginWithDeviceToken,
  logout,
  getCurrentUser,
  register,
  isAdmin,
  isMasterAdmin
};

export default authService;
